<template>
  <div class="align-items-center justify-content-center" style="display: flex">
    <div style="width: 100%; max-width: 1100px; min-width: 320px">
      <h2 style="text-align: center; margin-bottom: 12px">
        Ansprechpartner zusammenführen
      </h2>

      <Message severity="info" :closable="false" style="margin-bottom: 16px">
        Wähle den zu behaltenden Ansprechpartner und den doppelt erfassten. Der
        doppelt erfasste wird gelöscht; alle Mannschaften (Kapitän und
        Stellvertreter), Benutzerkonten und Vor-Saison-Anmeldungen werden auf den
        zu behaltenden umgehängt. Einzelne Angaben (E-Mail, Telefonnummer, …)
        lassen sich vorher übernehmen.
      </Message>

      <h4 v-if="error && error.length > 0" style="color: red">{{ error }}</h4>

      <div
        style="margin-bottom: 16px; border: 1px solid #dee2e6; border-radius: 6px; overflow: hidden"
      >
        <div
          style="display: flex; align-items: center; gap: 8px; padding: 10px 12px; background: #eef1f5; cursor: pointer"
          @click="showDuplicates = !showDuplicates"
        >
          <i :class="showDuplicates ? 'pi pi-chevron-down' : 'pi pi-chevron-right'" />
          <strong>Mögliche Dubletten</strong>
          <span
            v-if="!loadingDuplicates"
            style="background: #b3261e; color: #fff; border-radius: 10px; padding: 0 8px; font-size: 0.8em"
          >{{ duplicates.length }}</span>
          <i v-if="loadingDuplicates" class="pi pi-spin pi-spinner" />
          <span style="flex: 1" />
          <Button
            label="Neu suchen"
            icon="pi pi-refresh"
            severity="secondary"
            size="small"
            :disabled="loadingDuplicates || busy"
            @click.stop="loadDuplicates"
          />
        </div>
        <div v-if="showDuplicates" style="padding: 12px">
          <div v-if="loadingDuplicates">
            Suche nach ähnlichen Ansprechpartnern &nbsp;<i class="pi pi-spin pi-spinner" />
          </div>
          <div
            v-else-if="duplicates.length === 0"
            style="color: #6c757d; font-style: italic"
          >
            Keine möglichen Dubletten gefunden.
          </div>
          <table v-else style="width: 100%; border-collapse: collapse">
            <thead>
              <tr style="text-align: left; border-bottom: 2px solid #dee2e6">
                <th style="padding: 6px">Behalten (Vorschlag)</th>
                <th style="padding: 6px">Löschen (Vorschlag)</th>
                <th style="padding: 6px; white-space: nowrap">Ähnlichkeit</th>
                <th style="padding: 6px"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, idx) in duplicateRows"
                :key="idx"
                style="border-bottom: 1px solid #eef1f5; vertical-align: top"
              >
                <td style="padding: 6px">
                  <div v-if="row.keep">
                    <strong>{{ row.keep.name }}</strong>
                    <div style="font-size: 0.85em; color: #6c757d">
                      {{ contactDetails(row.keep) }}
                    </div>
                    <div style="font-size: 0.85em; color: #6c757d">
                      {{ teamNames(row.keep) }}
                    </div>
                  </div>
                  <div v-else style="color: #6c757d; font-style: italic">unbekannt</div>
                </td>
                <td style="padding: 6px">
                  <div v-if="row.remove">
                    <strong>{{ row.remove.name }}</strong>
                    <div style="font-size: 0.85em; color: #6c757d">
                      {{ contactDetails(row.remove) }}
                    </div>
                    <div style="font-size: 0.85em; color: #6c757d">
                      {{ teamNames(row.remove) }}
                    </div>
                  </div>
                  <div v-else style="color: #6c757d; font-style: italic">unbekannt</div>
                </td>
                <td style="padding: 6px; white-space: nowrap">
                  <span
                    :style="scoreBadgeStyle(row.pair.score)"
                    style="border-radius: 10px; padding: 1px 8px; color: #fff; font-size: 0.85em"
                  >{{ row.pair.score }} %</span>
                  <div
                    v-for="(reason, ri) in row.pair.reasons"
                    :key="ri"
                    style="font-size: 0.78em; color: #6c757d; margin-top: 2px"
                  >
                    {{ reason }}
                  </div>
                </td>
                <td style="padding: 6px; white-space: nowrap">
                  <Button
                    label="Übernehmen"
                    icon="pi pi-arrow-down"
                    size="small"
                    :disabled="busy"
                    @click="applyDuplicate(row.pair)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="grid">
        <div class="col-12 md:col-6">
          <h3 style="margin-bottom: 6px">Behalten</h3>
          <Dropdown
            v-model="targetContactPerson"
            :options="targetOptions"
            optionLabel="representation"
            dataKey="id"
            :filter="true"
            :filterFields="filterFields"
            placeholder="Ansprechpartner wählen"
            style="width: 100%"
            :disabled="busy"
          >
            <template #option="slotProps">
              <div>
                <strong>{{ slotProps.option.name }}</strong>
                <div style="font-size: 0.85em; color: #6c757d">
                  {{ contactDetails(slotProps.option) }}
                </div>
              </div>
            </template>
          </Dropdown>
          <div
            v-if="targetContactPerson && targetContactPerson.id"
            style="margin-top: 8px; padding: 10px; border: 1px solid #dee2e6; border-radius: 6px; background: #fff"
          >
            <div><strong>{{ targetContactPerson.name }}</strong></div>
            <div>{{ targetContactPerson.email }}</div>
            <div>{{ targetContactPerson.phoneNumber }}</div>
            <div v-if="teamNames(targetContactPerson)">
              {{ teamNames(targetContactPerson) }}
            </div>
          </div>
          <div v-else style="margin-top: 8px; color: #6c757d; font-style: italic">
            Noch kein Ansprechpartner gewählt
          </div>
        </div>
        <div class="col-12 md:col-6">
          <h3 style="margin-bottom: 6px; color: #b3261e">
            Doppelt erfasst (wird gelöscht)
          </h3>
          <Dropdown
            v-model="sourceContactPerson"
            :options="sourceOptions"
            optionLabel="representation"
            dataKey="id"
            :filter="true"
            :filterFields="filterFields"
            placeholder="Ansprechpartner wählen"
            style="width: 100%"
            :disabled="busy"
          >
            <template #option="slotProps">
              <div>
                <strong>{{ slotProps.option.name }}</strong>
                <div style="font-size: 0.85em; color: #6c757d">
                  {{ contactDetails(slotProps.option) }}
                </div>
              </div>
            </template>
          </Dropdown>
          <div
            v-if="sourceContactPerson && sourceContactPerson.id"
            style="margin-top: 8px; padding: 10px; border: 1px solid #dee2e6; border-radius: 6px; background: #fff"
          >
            <div><strong>{{ sourceContactPerson.name }}</strong></div>
            <div>{{ sourceContactPerson.email }}</div>
            <div>{{ sourceContactPerson.phoneNumber }}</div>
            <div v-if="teamNames(sourceContactPerson)">
              {{ teamNames(sourceContactPerson) }}
            </div>
          </div>
          <div v-else style="margin-top: 8px; color: #6c757d; font-style: italic">
            Noch kein Ansprechpartner gewählt
          </div>
        </div>
      </div>

      <div class="flex justify-content-center" style="margin-top: 12px">
        <Button
          label="Behalten und Löschen tauschen"
          icon="pi pi-arrows-h"
          severity="secondary"
          :disabled="(!sourceContactPerson && !targetContactPerson) || busy"
          @click="swapContactPersons"
        />
      </div>

      <div
        v-if="sourceContactPerson && targetContactPerson"
        style="margin-top: 20px; border: 1px solid #dee2e6; border-radius: 6px; overflow: hidden"
      >
        <div style="padding: 10px 12px; background: #eef1f5">
          <strong>Angaben übernehmen</strong>
          <div style="font-size: 0.85em; color: #6c757d; margin-top: 2px">
            Angehakte Werte werden vom zu löschenden in den behaltenen
            Ansprechpartner übertragen. Fehlende Angaben sind vorausgewählt,
            abweichende nicht.
          </div>
        </div>
        <table style="width: 100%; border-collapse: collapse">
          <thead>
            <tr style="text-align: left; border-bottom: 2px solid #dee2e6">
              <th style="padding: 6px 12px">Feld</th>
              <th style="padding: 6px 12px">Behalten</th>
              <th style="padding: 6px 12px; color: #b3261e">Wird gelöscht</th>
              <th style="padding: 6px 12px; white-space: nowrap">Übernehmen</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in fieldRows"
              :key="row.key"
              style="border-bottom: 1px solid #eef1f5; vertical-align: top"
              :style="row.conflict ? 'background: #fff8e6' : ''"
            >
              <td style="padding: 6px 12px">{{ row.label }}</td>
              <td
                style="padding: 6px 12px"
                :style="row.targetEmpty ? 'color: #6c757d; font-style: italic' : ''"
              >
                {{ row.targetEmpty ? "—" : row.targetText }}
              </td>
              <td
                style="padding: 6px 12px"
                :style="row.sourceEmpty ? 'color: #6c757d; font-style: italic' : ''"
              >
                {{ row.sourceEmpty ? "—" : row.sourceText }}
              </td>
              <td style="padding: 6px 12px; white-space: nowrap">
                <Checkbox
                  v-if="row.transferable"
                  v-model="fieldsFromSource"
                  :value="row.key"
                  :inputId="'transfer-' + row.key"
                  :disabled="busy"
                />
                <label
                  v-if="row.transferable && row.conflict"
                  :for="'transfer-' + row.key"
                  style="margin-left: 6px; font-size: 0.8em; color: #b06000; cursor: pointer"
                >überschreibt</label>
                <span
                  v-if="!row.transferable"
                  style="color: #6c757d; font-size: 0.8em"
                >{{ row.sourceEmpty ? "kein Wert" : "identisch" }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        v-if="sourceContactPerson && targetContactPerson"
        style="margin-top: 16px; padding: 10px; background: #f8f9fa; border-radius: 6px"
      >
        <div v-if="loadingCounts">
          Lade Auswirkung &nbsp;<i class="pi pi-spin pi-spinner" />
        </div>
        <div v-else-if="referenceCounts">
          Diese Aktion verschiebt:
          <ul style="margin: 6px 0 0 16px">
            <li>
              {{ referenceCounts.teamsAsPrimaryContact }} Mannschaft(en) mit
              Kapitäns-Zuordnung
            </li>
            <li>
              {{ referenceCounts.teamsAsSecondaryContact }} Mannschaft(en) mit
              Stellvertreter-Zuordnung
            </li>
            <li>{{ referenceCounts.users }} Benutzerkonto/-konten</li>
            <li>{{ referenceCounts.preSeasonTeams }} Vor-Saison-Anmeldung(en)</li>
          </ul>
        </div>
      </div>

      <div class="flex justify-content-center" style="margin-top: 20px; gap: 10px">
        <Button
          label="Zurücksetzen"
          severity="secondary"
          icon="pi pi-undo"
          :disabled="busy"
          @click="reset"
        />
        <Button
          label="Zusammenführen"
          icon="pi pi-link"
          severity="danger"
          :disabled="!canMerge || busy"
          @click="confirmDialog = true"
        />
      </div>

      <Dialog v-model:visible="confirmDialog" :closable="!busy" :modal="true">
        <template #header>
          <h3 style="margin: 0">Zusammenführen bestätigen</h3>
        </template>
        <div v-if="sourceContactPerson && targetContactPerson">
          <p>
            Der Ansprechpartner
            <strong>„{{ sourceContactPerson.name }}"</strong>
            wird unwiderruflich gelöscht. Alle Referenzen werden auf
            <strong>„{{ targetContactPerson.name }}"</strong> umgehängt.
          </p>
          <p v-if="referenceCounts">
            Betroffen: {{ referenceCounts.teamsAsPrimaryContact }} Kapitäns-,
            {{ referenceCounts.teamsAsSecondaryContact }} Stellvertreter-Zuordnung(en),
            {{ referenceCounts.users }} Benutzerkonto/-konten,
            {{ referenceCounts.preSeasonTeams }} Vor-Saison-Anmeldung(en).
          </p>
          <div v-if="selectedFieldRows.length > 0" style="margin-bottom: 16px">
            Folgende Angaben werden nach
            <strong>„{{ targetContactPerson.name }}"</strong> übertragen:
            <ul style="margin: 6px 0 0 16px">
              <li v-for="row in selectedFieldRows" :key="row.key">
                {{ row.label }}:
                <span v-if="row.targetEmpty" style="color: #6c757d">— </span>
                <span v-else><s>{{ row.targetText }}</s> </span>
                &rarr; <strong>{{ row.sourceText }}</strong>
              </li>
            </ul>
          </div>
          <p v-else style="color: #6c757d">
            Es werden keine Angaben übertragen; der behaltene Ansprechpartner
            bleibt unverändert.
          </p>
          <p>Soll fortgefahren werden?</p>
        </div>
        <template #footer>
          <Button
            label="Abbrechen"
            severity="secondary"
            icon="pi pi-times"
            :disabled="busy"
            @click="confirmDialog = false"
          />
          <Button
            label="Jetzt zusammenführen"
            icon="pi pi-link"
            severity="danger"
            :loading="busy"
            :disabled="busy"
            @click="merge"
          />
        </template>
      </Dialog>
    </div>
  </div>
</template>

<script>
import ServerConnection from "../../services/ServerConnection/ServerConnection";
import { useMenuService } from "../../services/MenuService/UseMenuService";
import Dropdown from "primevue/dropdown";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import Message from "primevue/message";
import Checkbox from "primevue/checkbox";

const TRANSFERABLE_FIELDS = [
  { key: "name", label: "Name" },
  { key: "email", label: "E-Mail" },
  { key: "phoneNumber", label: "Telefonnummer" },
  { key: "preferencedContactType", label: "Bevorzugter Kontaktweg" },
];

export default {
  name: "ContactPersonMergeView",
  components: { Dropdown, Button, Dialog, Message, Checkbox },
  data() {
    return {
      contactPersons: [],
      sourceContactPerson: null,
      targetContactPerson: null,
      referenceCounts: null,
      loadingCounts: false,
      busy: false,
      confirmDialog: false,
      error: "",
      filterFields: ["name", "email", "phoneNumber"],
      duplicates: [],
      loadingDuplicates: false,
      showDuplicates: true,
      fieldsFromSource: [],
    };
  },
  computed: {
    sourceOptions() {
      if (!this.targetContactPerson) {
        return this.contactPersons;
      }
      return this.contactPersons.filter((c) => c.id !== this.targetContactPerson.id);
    },
    targetOptions() {
      if (!this.sourceContactPerson) {
        return this.contactPersons;
      }
      return this.contactPersons.filter((c) => c.id !== this.sourceContactPerson.id);
    },
    canMerge() {
      return (
        this.sourceContactPerson &&
        this.targetContactPerson &&
        this.sourceContactPerson.id !== this.targetContactPerson.id
      );
    },
    duplicateRows() {
      return this.duplicates.map((pair) => ({
        pair,
        keep: this.contactPersonById(pair.keepId),
        remove: this.contactPersonById(pair.removeId),
      }));
    },
    fieldRows() {
      if (!this.sourceContactPerson || !this.targetContactPerson) {
        return [];
      }
      return TRANSFERABLE_FIELDS.map((field) => {
        var sourceEmpty = !this.hasFieldValue(this.sourceContactPerson, field.key);
        var targetEmpty = !this.hasFieldValue(this.targetContactPerson, field.key);
        var equal =
          !sourceEmpty &&
          !targetEmpty &&
          this.fieldsEqual(this.sourceContactPerson, this.targetContactPerson, field.key);
        return {
          key: field.key,
          label: field.label,
          sourceEmpty: sourceEmpty,
          targetEmpty: targetEmpty,
          sourceText: this.fieldText(this.sourceContactPerson, field.key),
          targetText: this.fieldText(this.targetContactPerson, field.key),
          transferable: !sourceEmpty && !equal,
          conflict: !sourceEmpty && !targetEmpty && !equal,
        };
      });
    },
    selectedFieldRows() {
      return this.fieldRows.filter(
        (row) => row.transferable && this.fieldsFromSource.indexOf(row.key) >= 0
      );
    },
  },
  watch: {
    sourceContactPerson() {
      this.loadReferenceCounts();
      this.initFieldSelection();
    },
    targetContactPerson() {
      this.initFieldSelection();
    },
  },
  mounted() {
    const menuService = useMenuService();
    menuService.setMainMenuItemNames(["home"]);
    this.loadContactPersons();
  },
  methods: {
    contactPersonById(id) {
      return this.contactPersons.find((c) => c.id === id) || null;
    },
    scoreBadgeStyle(score) {
      if (score >= 95) return { background: "#b3261e" };
      if (score >= 85) return { background: "#e08600" };
      return { background: "#6c757d" };
    },
    contactDetails(contactPerson) {
      if (!contactPerson) {
        return "";
      }
      var details = [];
      if (contactPerson.email) {
        details.push(contactPerson.email);
      }
      if (contactPerson.phoneNumber) {
        details.push(contactPerson.phoneNumber);
      }
      return details.join(" · ");
    },
    teamNames(contactPerson) {
      if (!contactPerson) {
        return "";
      }
      var names = [];
      (contactPerson.primaryTeams || []).forEach((team) => {
        names.push(team.name || team.representation);
      });
      (contactPerson.secondaryTeams || []).forEach((team) => {
        names.push((team.name || team.representation) + " (Stellv.)");
      });
      return names.join(", ");
    },
    hasFieldValue(contactPerson, key) {
      if (!contactPerson) {
        return false;
      }
      var value = contactPerson[key];
      if (value === null || value === undefined) {
        return false;
      }
      if (typeof value === "string") {
        return value.trim().length > 0;
      }
      return true;
    },
    fieldsEqual(a, b, key) {
      var valueA = a[key];
      var valueB = b[key];
      if (typeof valueA === "string" && typeof valueB === "string") {
        return valueA.trim() === valueB.trim();
      }
      return valueA === valueB;
    },
    fieldText(contactPerson, key) {
      if (!this.hasFieldValue(contactPerson, key)) {
        return "";
      }
      return String(contactPerson[key]);
    },
    initFieldSelection() {
      this.fieldsFromSource = this.fieldRows
        .filter((row) => row.transferable && row.targetEmpty)
        .map((row) => row.key);
    },
    fetchContactPersons() {
      return ServerConnection.get("data/contactperson").then((response) => {
        var list = Array.isArray(response.data) ? response.data : [];
        list.sort((a, b) =>
          (a.representation || a.name || "").localeCompare(
            b.representation || b.name || ""
          )
        );
        this.contactPersons = list;
        return list;
      });
    },
    loadContactPersons() {
      this.fetchContactPersons()
        .then(() => {
          this.loadDuplicates();
        })
        .catch((err) => {
          this.error = "Konnte Ansprechpartner nicht laden: " + this.formatError(err);
        });
    },
    reloadContactPersons(reselectTargetId) {
      this.fetchContactPersons()
        .then(() => {
          if (reselectTargetId) {
            this.targetContactPerson =
              this.contactPersonById(reselectTargetId) || this.targetContactPerson;
          }
        })
        .catch((err) => {
          this.error =
            "Konnte Ansprechpartner nicht neu laden: " + this.formatError(err);
        });
    },
    loadDuplicates() {
      this.loadingDuplicates = true;
      ServerConnection.get("admin/contactperson/duplicatePairs")
        .then((response) => {
          this.duplicates = Array.isArray(response.data) ? response.data : [];
        })
        .catch((err) => {
          this.error = "Konnte Dubletten nicht ermitteln: " + this.formatError(err);
        })
        .finally(() => {
          this.loadingDuplicates = false;
        });
    },
    applyDuplicate(pair) {
      if (this.busy) {
        return;
      }
      this.sourceContactPerson = null;
      this.targetContactPerson = this.contactPersonById(pair.keepId);
      this.sourceContactPerson = this.contactPersonById(pair.removeId);
      this.error = "";
    },
    swapContactPersons() {
      var previousSource = this.sourceContactPerson;
      this.sourceContactPerson = this.targetContactPerson;
      this.targetContactPerson = previousSource;
    },
    loadReferenceCounts() {
      this.referenceCounts = null;
      if (!this.sourceContactPerson || !this.sourceContactPerson.id) {
        return;
      }
      this.loadingCounts = true;
      var requestedId = this.sourceContactPerson.id;
      ServerConnection.get("admin/contactperson/" + requestedId + "/references")
        .then((response) => {
          if (this.sourceContactPerson && this.sourceContactPerson.id === requestedId) {
            this.referenceCounts = response.data;
          }
        })
        .catch((err) => {
          if (this.sourceContactPerson && this.sourceContactPerson.id === requestedId) {
            this.error = "Konnte Auswirkung nicht ermitteln: " + this.formatError(err);
          }
        })
        .finally(() => {
          if (this.sourceContactPerson && this.sourceContactPerson.id === requestedId) {
            this.loadingCounts = false;
          }
        });
    },
    merge() {
      if (!this.canMerge || this.busy) {
        return;
      }
      this.busy = true;
      this.error = "";
      var sourceId = this.sourceContactPerson.id;
      var sourceName = this.sourceContactPerson.name;
      var targetId = this.targetContactPerson.id;
      var targetName = this.targetContactPerson.name;
      ServerConnection.post("admin/contactperson/merge", {
        sourceId: sourceId,
        targetId: targetId,
        fieldsFromSource: this.selectedFieldRows.map((row) => row.key),
      })
        .then((response) => {
          var result = response.data || {};
          var transferred = result.transferredFields || [];
          this.busy = false;
          this.confirmDialog = false;
          this.$toast.add({
            severity: "success",
            summary: "Zusammengeführt",
            detail:
              '"' + sourceName + '" wurde in "' + targetName + '" überführt. ' +
              (result.teamsAsPrimaryContact || 0) + " Kapitäns-, " +
              (result.teamsAsSecondaryContact || 0) + " Stellvertreter-Zuordnung(en), " +
              (result.users || 0) + " Benutzerkonto/-konten, " +
              (result.preSeasonTeams || 0) + " Vor-Saison-Anmeldung(en) angepasst." +
              (transferred.length > 0
                ? " Übernommen: " + transferred.join(", ") + "."
                : ""),
            life: 6000,
          });
          this.contactPersons = this.contactPersons.filter((c) => c.id !== sourceId);
          this.duplicates = this.duplicates.filter(
            (p) => p.keepId !== sourceId && p.removeId !== sourceId
          );
          this.sourceContactPerson = null;
          this.referenceCounts = null;
          this.reloadContactPersons(targetId);
        })
        .catch((err) => {
          this.busy = false;
          this.error = "Zusammenführen fehlgeschlagen: " + this.formatError(err);
        });
    },
    reset() {
      this.sourceContactPerson = null;
      this.targetContactPerson = null;
      this.referenceCounts = null;
      this.fieldsFromSource = [];
      this.error = "";
    },
    formatError(error) {
      if (error && error.response && error.response.data) {
        var data = error.response.data;
        if (typeof data === "string" && data.length > 0) {
          return data;
        }
      }
      if (error && error.message) {
        return error.message;
      }
      return "Unbekannter Fehler";
    },
  },
};
</script>
